/**
 * A Wallet on the battlefield.
 *
 * Wallets are not cards you read, they are a resource you count, so they do not
 * use the 5:7 card frame. UI_UX section 7 budgets a 64px Wallet row, which a
 * card frame cannot fill legibly: at that height a frame is 46px wide and every
 * piece of text in it truncates. A square tile fits the budget and says the
 * three things that matter: which chain, how much it makes, and whether it is
 * still ready.
 *
 * The chain comes from the Wallet's own `wallet` block, never from its name.
 */

import type { MouseEvent } from 'react';
import type { Chain, GameState } from '@/engine';
import { profile } from '@/engine';
import { FactionGlyph } from '@/ui/icons/FactionGlyph';
import styles from './WalletTile.module.css';

export interface WalletTileProps {
  state: GameState;
  instanceId: string;
  validTarget?: boolean;
  dimmed?: boolean;
  selected?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}

export function WalletTile({
  state,
  instanceId,
  validTarget,
  dimmed,
  selected,
  onClick,
  onContextMenu,
  onPointerEnter,
  onPointerLeave,
}: WalletTileProps) {
  const object = state.objects[instanceId];
  if (!object) return null;

  const prof = profile(state, instanceId);
  const chain = prof.wallet?.chain ?? 'any';
  const faction = chain === 'any' ? 'neutral' : (chain as Chain);
  const produces = prof.wallet?.produces ?? 1;
  const ready = !object.exhausted;

  const status = object.frozen ? 'Frozen' : ready ? 'Ready' : 'Spent';
  const label = `${prof.name}, ${status}, produces ${produces}`;

  return (
    <div
      className={[
        styles.tile,
        ready ? styles.ready : styles.spent,
        object.frozen ? styles.frozen : '',
        validTarget ? styles.validTarget : '',
        dimmed ? styles.dimmed : '',
        selected ? styles.selected : '',
        onClick ? styles.clickable : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-faction={faction}
      data-instance-id={instanceId}
      data-card-id={object.cardId}
      data-wallet-ready={ready}
      data-testid={`wallet-${instanceId}`}
      role={onClick ? 'button' : 'group'}
      tabIndex={onClick ? 0 : undefined}
      aria-label={label}
      title={label}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                (e.currentTarget as HTMLDivElement).click();
              }
            }
          : undefined
      }
    >
      <span className={styles.glyph}>
        <FactionGlyph faction={faction} size={22} />
      </span>
      <span className={styles.chain}>{chainLabel(chain)}</span>
      {produces > 1 ? <span className={styles.produces}>{`×${produces}`}</span> : null}
      <span className={styles.state} aria-hidden="true" />
    </div>
  );
}

/** Three or four characters, so it never truncates inside the tile. */
function chainLabel(chain: Chain | 'any'): string {
  return chain === 'any' ? 'ANY' : chain.toUpperCase();
}
